// Tenantra Inc. - Terms of Use (TransUnion-style industry standard formatting)
import { ArrowLeft, Building2, FileText, Printer } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { useState, useRef } from 'react';

const TOC_ITEMS = [
  { id: 'terms-notice', label: 'Terms of Use Notice' },
  { id: 'acceptance', label: '1. Acceptance of Terms' },
  { id: 'platform-role', label: '2. Platform Role' },
  { id: 'account-registration', label: '3. Account Registration' },
  { id: 'landlord-obligations', label: '4. Landlord Obligations' },
  { id: 'tenant-rights', label: '5. Tenant Rights' },
  { id: 'dispute-resolution', label: '6. Dispute Resolution' },
  { id: 'nature-of-reports', label: '7. Nature of Reports' },
  { id: 'prohibited-conduct', label: '8. Prohibited Conduct' },
  { id: 'intellectual-property', label: '9. Intellectual Property' },
  { id: 'fees-payments', label: '10. Fees and Payments' },
  { id: 'service-availability', label: '11. Service Availability' },
  { id: 'limitation-liability', label: '12. Limitation of Liability' },
  { id: 'indemnification', label: '13. Indemnification' },
  { id: 'termination', label: '14. Termination' },
  { id: 'governing-law', label: '15. Governing Law' },
  { id: 'sms-terms', label: '16. SMS Messaging Terms' },
  { id: 'changes', label: '17. Changes to Terms' },
  { id: 'contact', label: '18. Contact' },
];

export default function TermsOfServicePage() {
  const [activeSection, setActiveSection] = useState('');
  const contentRef = useRef(null);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-40 print:hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3.5">
          <div className="flex items-center justify-between">
            <Logo size="md" />
            <div className="flex items-center gap-3">
              <button
                onClick={() => window.print()}
                className="text-xs text-stone-500 hover:text-stone-700 flex items-center gap-1.5 px-3 py-1.5 border border-stone-200 rounded-lg hover:bg-stone-50 transition-colors"
                data-testid="print-btn"
              >
                <Printer className="w-3.5 h-3.5" />
                Print
              </button>
              <a href="/" className="text-sm text-stone-500 hover:text-teal-600 flex items-center gap-1.5 transition-colors" data-testid="back-home-link">
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Home</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Main layout */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10 print:py-4 print:max-w-none">
        <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-10">

          {/* Sidebar TOC */}
          <aside className="hidden lg:block print:hidden">
            <nav className="sticky top-20">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400 mb-3">On this page</p>
              <ul className="space-y-0.5 max-h-[calc(100vh-120px)] overflow-y-auto pr-1">
                {TOC_ITEMS.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => scrollToSection(item.id)}
                      className={`block w-full text-left text-[13px] leading-snug py-1.5 px-2.5 rounded transition-colors ${
                        activeSection === item.id
                          ? 'text-teal-700 bg-teal-50 font-medium'
                          : 'text-stone-500 hover:text-stone-800 hover:bg-stone-50'
                      }`}
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Document body */}
          <article ref={contentRef}>
            {/* Title block */}
            <div className="mb-8 sm:mb-10 pb-6 border-b border-stone-200">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-5 h-5 text-teal-600" />
                <span className="text-xs font-semibold uppercase tracking-wider text-teal-600">Legal</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-2" data-testid="terms-title">Terms of Service</h1>
              <p className="text-sm text-stone-500">(Last updated April 2026)</p>
            </div>

            {/* ===== TERMS NOTICE ===== */}
            <section id="terms-notice" className="mb-10 scroll-mt-24">
              <div className="bg-stone-50 border border-stone-200 rounded-lg overflow-hidden">
                <div className="bg-stone-800 px-5 py-3">
                  <h2 className="text-sm font-bold text-white uppercase tracking-wide">Tenantra Inc. Terms of Use Notice</h2>
                </div>
                <div className="px-5 py-5 space-y-5">
                  <p className="text-sm text-stone-700 leading-relaxed">
                    By accessing or using the Tenantra platform, you agree to be bound by these Terms of Service. These Terms constitute a legally binding agreement between you and Tenantra Inc.
                  </p>
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-1.5">Platform</h3>
                    <p className="text-sm text-stone-700 leading-relaxed">
                      Tenantra facilitates identity-verified, consent-based rental history records submitted by landlords. We do not independently verify all submitted information or guarantee its accuracy.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-1.5">Key Terms</h3>
                    <ul className="text-sm text-stone-700 space-y-1 list-disc pl-5">
                      <li>Only landlords may create accounts and submit reports after identity verification.</li>
                      <li>Tenants must approve onboarding before any record is created.</li>
                      <li>Users must not submit false or misleading information.</li>
                      <li>The Service is provided "as is" without guarantees.</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-1.5">Tenant Rights</h3>
                    <ul className="text-sm text-stone-700 space-y-1 list-disc pl-5">
                      <li>View rental records, dispute reports, and request corrections.</li>
                      <li>Withdraw consent for future participation (subject to limitations).</li>
                    </ul>
                  </div>
                  <p className="text-xs text-stone-500 pt-2 border-t border-stone-200">
                    For the full terms, please read the complete Terms of Service below.
                  </p>
                </div>
              </div>
            </section>

            {/* ===== FULL TERMS ===== */}

            {/* 1. Acceptance of Terms */}
            <section id="acceptance" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">1. Acceptance of Terms</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                By accessing or using the Tenantra platform ("<strong>Service</strong>"), you agree to be bound by these Terms of Service ("<strong>Terms</strong>"). If you do not agree, do not use the Service.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                These Terms constitute a legally binding agreement between you and Tenantra Inc.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed">
                You must be at least 18 years of age to use the Service.
              </p>
            </section>

            {/* 2. Platform Role */}
            <section id="platform-role" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">2. Platform Role</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                Tenantra operates as a platform that facilitates identity-verified, consent-based rental history records submitted by landlords.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">
                Information on the platform consists of user-submitted data relating to rental relationships and post-tenancy reporting.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed mb-2">Tenantra:</p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5">
                <li>Does not independently verify all submitted information</li>
                <li>Does not act as a government registry, land title authority, or credit reporting agency</li>
                <li>Does not guarantee the accuracy, completeness, or reliability of user-submitted data</li>
              </ul>
            </section>

            {/* 3. Account Registration */}
            <section id="account-registration" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">3. Account Registration</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">
                To access features, you must create an account. You agree to:
              </p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Provide accurate information</li>
                <li>Maintain account security</li>
                <li>Accept responsibility for all activity under your account</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Account Types</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Landlord Accounts only are permitted to submit reports</li>
                <li>Tenant records are created through landlord onboarding and consent</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Verification</h3>
              <p className="text-sm text-stone-700 leading-relaxed">
                Landlords must complete identity verification and provide supporting documentation before submitting reports.
              </p>
            </section>

            {/* 4. Landlord Obligations */}
            <section id="landlord-obligations" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">4. Landlord Obligations</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">Landlords agree to:</p>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Accurate Reporting</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Submit truthful information</li>
                <li>Base reports on actual tenancy experience</li>
                <li>Maintain supporting records</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Consent Requirement</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Obtain tenant consent before onboarding</li>
                <li>Not report tenants without consent</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Non-Discrimination</h3>
              <p className="text-sm text-stone-700 leading-relaxed mb-5">
                Landlords must comply with Canadian human rights laws.
              </p>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Authorization</h3>
              <p className="text-sm text-stone-700 leading-relaxed">
                Only report on properties they are authorized to manage.
              </p>
            </section>

            {/* 5. Tenant Rights */}
            <section id="tenant-rights" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">5. Tenant Rights</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">Tenants have the right to:</p>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Access</h3>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">View their rental record.</p>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Dispute</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-4">
                <li>Challenge reports</li>
                <li>Submit supporting evidence</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Correction</h3>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">Request updates or corrections.</p>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Consent</h3>
              <p className="text-sm text-stone-700 leading-relaxed">
                Withdraw consent for future participation (subject to limitations).
              </p>
            </section>

            {/* 6. Dispute Resolution Process */}
            <section id="dispute-resolution" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">6. Dispute Resolution Process</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-2">When a dispute is submitted:</p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-4">
                <li>Reports may be flagged</li>
                <li>Supporting information may be reviewed</li>
                <li>Tenantra may update, restrict, or remove content</li>
              </ul>
              <div className="bg-stone-50 border-l-4 border-stone-800 px-4 py-3">
                <p className="text-sm text-stone-800 font-semibold">
                  Tenantra reserves the sole right to determine outcomes based on available information and platform policies.
                </p>
              </div>
            </section>

            {/* 7. Nature of Reports */}
            <section id="nature-of-reports" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">7. Nature of Reports</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-2">Reports on Tenantra:</p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-4">
                <li>Are submitted by verified landlords</li>
                <li>Reflect user-reported rental history</li>
                <li>Are not independently verified by Tenantra</li>
              </ul>
              <p className="text-sm text-stone-700 leading-relaxed">
                Reports are intended to support decision-making and should not be interpreted as definitive or guaranteed statements of fact.
              </p>
            </section>

            {/* 8. Prohibited Conduct */}
            <section id="prohibited-conduct" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">8. Prohibited Conduct</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">Users must not:</p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5">
                <li>Submit false or misleading information</li>
                <li>Misuse the platform</li>
                <li>Engage in harassment or discrimination</li>
                <li>Attempt to bypass security</li>
              </ul>
            </section>

            {/* 9. Intellectual Property */}
            <section id="intellectual-property" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">9. Intellectual Property</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                All platform content belongs to Tenantra Inc.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed">
                Users retain ownership of submitted content but grant Tenantra a non-exclusive, worldwide license to use it for platform operation.
              </p>
            </section>

            {/* 10. Fees and Payments */}
            <section id="fees-payments" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">10. Fees and Payments</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">
                Paid features may be offered. Fees:
              </p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5">
                <li>Are charged in CAD</li>
                <li>May be recurring</li>
                <li>Are non-refundable unless stated otherwise</li>
              </ul>
            </section>

            {/* 11. Service Availability */}
            <section id="service-availability" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">11. Service Availability</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">
                We do not guarantee uninterrupted service. We may:
              </p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5">
                <li>Modify features</li>
                <li>Suspend functionality</li>
                <li>Perform maintenance</li>
              </ul>
            </section>

            {/* 12. Limitation of Liability */}
            <section id="limitation-liability" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">12. Limitation of Liability</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-4 uppercase font-medium tracking-wide">
                The Service is provided "as is" without warranties of any kind.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed mb-2">Tenantra is not liable for:</p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5">
                <li>Decisions made based on platform data</li>
                <li>Inaccuracies in user-submitted content</li>
                <li>Indirect or consequential damages</li>
              </ul>
            </section>

            {/* 13. Indemnification */}
            <section id="indemnification" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">13. Indemnification</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-2">
                You agree to indemnify and hold harmless Tenantra Inc. from any claims, damages, or expenses arising from:
              </p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5">
                <li>Your use of the Service</li>
                <li>Content you submit</li>
                <li>Violations of law or third-party rights</li>
              </ul>
            </section>

            {/* 14. Termination */}
            <section id="termination" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">14. Termination</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-2">
                We may suspend or terminate accounts for:
              </p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-3">
                <li>Violations of these Terms</li>
                <li>Misuse of the platform</li>
                <li>Non-payment</li>
              </ul>
              <p className="text-sm text-stone-700 leading-relaxed">
                Data may be retained as required by law or for dispute resolution purposes.
              </p>
            </section>

            {/* 15. Governing Law */}
            <section id="governing-law" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">15. Governing Law</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                These Terms are governed by the laws of Ontario, Canada.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed">
                Disputes will be resolved through arbitration in Toronto.
              </p>
            </section>

            {/* 16. SMS Messaging Terms */}
            <section id="sms-terms" className="mb-9 scroll-mt-24" data-testid="terms-sms-section">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">16. SMS Messaging Terms</h2>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5">
                <li><strong>Program:</strong> Tenantra Account Notifications. By checking the SMS consent box during signup, you agree to receive text messages from Tenantra Inc. at the mobile number you provide.</li>
                <li><strong>Message types:</strong> verification codes, rent reminders, payment confirmations, and lease alerts. No marketing messages are sent through this program.</li>
                <li><strong>Frequency:</strong> message frequency varies based on your account activity.</li>
                <li><strong>Cost:</strong> message and data rates may apply. Carriers are not liable for delayed or undelivered messages.</li>
                <li><strong>Opt out:</strong> reply <strong>STOP</strong> at any time to unsubscribe. You will receive a final confirmation message.</li>
                <li><strong>Help:</strong> reply <strong>HELP</strong> or email <a href="mailto:support@tenantra.ca" className="text-teal-700 underline">support@tenantra.ca</a>.</li>
                <li><strong>Optional:</strong> consent to receive texts is not required to create an account or use Tenantra.</li>
                <li><strong>Privacy:</strong> mobile numbers and SMS consent are never sold or shared with third parties for marketing. See our <a href="/privacy-policy#sms-messaging" className="text-teal-700 underline">Privacy Policy</a>.</li>
              </ul>
            </section>

            {/* 17. Changes to Terms */}
            <section id="changes" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">17. Changes to Terms</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                We may update these Terms at any time.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed">
                Continued use of the platform constitutes acceptance of any updates.
              </p>
            </section>

            {/* 18. Contact */}
            <section id="contact" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">18. Contact</h2>
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-5 text-sm text-stone-700">
                <p className="font-bold text-stone-800 mb-1">Tenantra Inc.</p>
                <p>Winnipeg, Manitoba, Canada</p>
                <p className="mt-2">Email: <a href="mailto:support@tenantra.ca" className="text-teal-700 underline">support@tenantra.ca</a></p>
              </div>
            </section>

            {/* Document footer */}
            <div className="pt-6 mt-6 border-t border-stone-200 text-center">
              <p className="text-xs text-stone-400">
                &copy; {new Date().getFullYear()} Tenantra Inc. All rights reserved.
              </p>
            </div>
          </article>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          @page { margin: 0.75in; size: letter; }
        }
      `}</style>
    </div>
  );
}
